import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogTitle,
    IconButton,
    Popover,
    Skeleton,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createContext, useContext, useEffect, useState } from 'react';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import { shortName } from './taskUi';
import {
    DATE_FIELDS,
    FIELD_LABELS,
    STATUS_OPTIONS,
    TYPE_OPTIONS,
    datePresets,
    listValues,
    parseRange,
    rangeValue,
    toggleValue,
} from './taskFilters';

// Phones get full-size value buttons, so each is a 44px tap target
const NarrowContext = createContext(false);

const ValueButton = ({ label, on, onClick }) => {
    const narrow = useContext(NarrowContext);
    return (
        <Chip
            size={narrow ? 'medium' : 'small'}
            sx={narrow ? { minHeight: 44 } : undefined}
            label={label}
            icon={on ? <CheckIcon /> : undefined}
            color={on ? 'primary' : 'default'}
            variant={on ? 'filled' : 'outlined'}
            onClick={onClick}
            aria-pressed={on}
        />
    );
};

const Field = ({ label, children }) => (
    <Box component="fieldset" sx={{ border: 0, p: 0, m: 0, mb: 2 }}>
        <Typography component="legend" variant="subtitle2" sx={{ mb: 0.75 }}>
            {label}
        </Typography>
        {children}
    </Box>
);

// The panel's own copy of a custom range, so typing one date doesn't reload the list before the other
const DateField = ({ field, value, onChange }) => {
    const presets = datePresets(field);
    const isPreset = presets.some((p) => p.value === value);
    const [custom, setCustom] = useState(!!value && !isPreset);
    const [range, setRange] = useState(parseRange(isPreset ? '' : value));
    useEffect(() => {
        if (!value) setCustom(false);
        if (value && !presets.some((p) => p.value === value)) setRange(parseRange(value));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    const setRangeValue = (next) => {
        setRange(next);
        if (!next.from || !next.to || next.from <= next.to) onChange(rangeValue(next.from, next.to));
    };
    return (
        <Field label={FIELD_LABELS[field]}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {presets.map((p) => (
                    <ValueButton
                        key={p.value}
                        label={p.label}
                        on={value === p.value}
                        onClick={() => {
                            setCustom(false);
                            onChange(value === p.value ? undefined : p.value);
                        }}
                    />
                ))}
                <ValueButton
                    label="Custom range"
                    on={custom}
                    onClick={() => {
                        if (custom) {
                            setCustom(false);
                            onChange(undefined);
                        } else {
                            setCustom(true);
                            setRange({ from: '', to: '' });
                            if (isPreset) onChange(undefined);
                        }
                    }}
                />
            </Box>
            {custom && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        type="date"
                        label="From"
                        InputLabelProps={{ shrink: true }}
                        value={range.from}
                        onChange={(e) => setRangeValue({ ...range, from: e.target.value })}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="To"
                        InputLabelProps={{ shrink: true }}
                        value={range.to}
                        error={!!range.from && !!range.to && range.to < range.from}
                        helperText={
                            range.from && range.to && range.to < range.from ? 'The end is before the start' : ' '
                        }
                        onChange={(e) => setRangeValue({ ...range, to: e.target.value })}
                    />
                </Box>
            )}
        </Field>
    );
};

/**
 * The Filters panel (rev6 Part 4, 6e): a set list of fields, each with its values as buttons.
 * A click turns a value on or off and the list updates at once. Not a query builder.
 *
 * fields: which fields this view has (panelFields). facets: { languages, stages, assignees, pms }.
 * matchText: "8 tasks match", or on the calendar "8 tasks in November".
 */
const FACET_FIELDS = ['language', 'stage'];

const TaskFiltersPanel = ({
    anchorEl,
    open,
    onClose,
    fields,
    state,
    onChange,
    onClearAll,
    facets,
    facetsFailed,
    onRetryFacets,
    categories,
    platforms,
    matchText,
}) => {
    useTrackUsage('TaskFiltersPanel', 'dialog', 'tasks', open);
    const theme = useTheme();
    const narrow = useMediaQuery(theme.breakpoints.down('sm'));

    const optionsFor = (field) => {
        switch (field) {
            case 'language':
                return facets?.languages || [];
            case 'stage':
                return facets?.stages || [];
            case 'category':
                return (categories || []).map((c) => ({
                    key: c.key,
                    label: c.hidden ? `${c.label} (hidden)` : c.label,
                }));
            case 'contentType':
                return TYPE_OPTIONS;
            case 'platform':
                return (platforms || []).map((p) => ({
                    key: p.key,
                    label: p.hidden ? `${p.label} (hidden)` : p.label,
                }));
            case 'status':
                return STATUS_OPTIONS;
            default:
                return [];
        }
    };

    const listField = (field) => {
        const selected = listValues(state[field]);
        const options = optionsFor(field);
        // A value from a link or saved filter that no task has right now still shows, so it can be turned off
        const extra = selected.filter((v) => !options.some((o) => o.key === v)).map((v) => ({ key: v, label: v }));
        const all = [...options, ...extra];
        if (FACET_FIELDS.includes(field) && !facets && !facetsFailed) {
            return (
                <Field key={field} label={FIELD_LABELS[field]}>
                    <Skeleton variant="rounded" height={28} />
                </Field>
            );
        }
        if (all.length === 0) return null;
        return (
            <Field key={field} label={FIELD_LABELS[field]}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {all.map((o) => (
                        <ValueButton
                            key={o.key}
                            label={o.label}
                            on={selected.includes(o.key)}
                            onClick={() => onChange({ [field]: toggleValue(state[field], o.key) })}
                        />
                    ))}
                </Box>
            </Field>
        );
    };

    const peopleField = (field) => {
        const people = (field === 'assignee' ? facets?.assignees : facets?.pms) || [];
        const selected = listValues(state[field]);
        return (
            <Field key={field} label={FIELD_LABELS[field]}>
                <Autocomplete
                    multiple
                    size="small"
                    options={[...new Set([...people, ...selected])]}
                    value={selected}
                    getOptionLabel={(email) => `${shortName(email)} (${email})`}
                    renderTags={(value, getTagProps) =>
                        value.map((email, index) => (
                            <Chip size="small" label={shortName(email)} {...getTagProps({ index })} key={email} />
                        ))
                    }
                    onChange={(_, value) => onChange({ [field]: value.length ? value.join(',') : undefined })}
                    renderInput={(params) => (
                        <TextField {...params} placeholder={selected.length ? '' : 'Pick people'} />
                    )}
                />
            </Field>
        );
    };

    const footer = (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 2,
                pt: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
            }}
        >
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }} aria-live="polite">
                {matchText || ''}
            </Typography>
            <Button size={narrow ? 'medium' : 'small'} onClick={onClearAll}>
                Clear all
            </Button>
            <Button size={narrow ? 'medium' : 'small'} variant="contained" onClick={onClose}>
                Done
            </Button>
        </Box>
    );

    const fieldsBody = (
        <Box
            sx={{
                p: 2,
                pb: 0,
                width: narrow ? 'auto' : 440,
                flex: narrow ? 1 : undefined,
                maxHeight: narrow ? 'none' : '60vh',
                overflowY: 'auto',
            }}
        >
            {facetsFailed && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={onRetryFacets}>
                            Try again
                        </Button>
                    }
                >
                    Couldn't load languages and stages.
                </Alert>
            )}
            {fields.map((f) => {
                if (DATE_FIELDS.includes(f))
                    return <DateField key={f} field={f} value={state[f]} onChange={(v) => onChange({ [f]: v })} />;
                if (f === 'assignee' || f === 'pm') return peopleField(f);
                return listField(f);
            })}
        </Box>
    );

    // The count and Done stay in view while the fields scroll
    if (narrow) {
        return (
            <NarrowContext.Provider value>
                <Dialog open={open} onClose={onClose} fullScreen disableRestoreFocus aria-labelledby="filters-title">
                    <DialogTitle id="filters-title" sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
                        <Box component="span" sx={{ flex: 1 }}>
                            Filters
                        </Box>
                        <IconButton aria-label="Close filters" onClick={onClose} sx={{ minWidth: 44, minHeight: 44 }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    {fieldsBody}
                    {footer}
                </Dialog>
            </NarrowContext.Provider>
        );
    }
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            disableRestoreFocus
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { 'aria-label': 'Filters' } }}
        >
            {fieldsBody}
            {footer}
        </Popover>
    );
};

export default TaskFiltersPanel;
